/**
 * WelcomePage - Application Entry Point & Authentication Handler
 * 
 * PRIMARY RESPONSIBILITIES:
 * 1. Display splash screen and marketing content to unauthenticated users
 * 2. Auto-detect if user is already logged in and restore session
 * 3. Handle user login form submission and validation
 * 4. Prepare user session after successful authentication
 * 5. Determine and navigate to appropriate page based on account status
 * 6. Handle authentication errors and display to user
 * 7. Publish LOGIN event to notify app shell to update menu
 * 
 * NAVIGATION LOGIC:
 * - Expired/Cancelled accounts → /accessDenied
 * - Active accounts → /dashboard
 * - No user/invalid token → /login
 * - Authentication errors → Stay on /welcome with error message
 * 
 * DOES NOT:
 * - Manage side menu structure (delegated to AppComponent)
 * - Handle menu item clicks (delegated to AppComponent)
 * - Perform route protection (delegated to AuthGuard)
 * 
 * LIFECYCLE:
 * - Loaded as the initial route when app starts
 * - Displays for 3-5 seconds (splash screen)
 * - Auto-navigates away after detecting authentication state
 * - Destroyed after successful navigation
 * 
 * EVENT FLOW:
 * 1. Page loads and displays splash screen
 * 2. Checks if user is already authenticated
 * 3. If authenticated:
 *    a. Prepares session (loads dealership, settings, etc.)
 *    b. Publishes EVENT_LOGIN for AppComponent to update menu
 *    c. Navigates to appropriate page based on account status
 * 4. If not authenticated:
 *    a. Shows marketing slides
 *    b. User clicks Login button
 *    c. Navigates to /login page
 */
/* eslint-disable no-console */

import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, MenuController } from '@ionic/angular';

import { ViewBasePage } from '@shared/pages';
import { AuthService, LoaderService, LocalStorageService } from '@shared/services';
import { Constants } from '@shared/models';

@Component({
    selector: 'app-welcome',
    templateUrl: './welcome.page.html',
    styleUrls: ['./welcome.page.scss'],
    standalone: true,
    imports: [ CommonModule, FormsModule, IonicModule ]
})
export class WelcomePage extends ViewBasePage implements OnInit, OnDestroy {
    public vm = { loginText: 'Login' };
    public splash = true;
    private userToken: string | null = null;
    private forcePromptForLoginCredentials = false;

    constructor(
        // 
        private authService: AuthService,
        private loaderService: LoaderService,
        private localStorageService: LocalStorageService,
        private menuController: MenuController,
        injector: Injector
    ) {
        super(injector);
        super.setCurrentView(Constants.VIEW_WELCOME);
    }

    /**
     * Handle slide changes in marketing carousel
     */
    // public onSlideChange(event: any): void {
    //     console.log('Slide changed:', event);
    // }

    /**
     * Initialize the welcome page
     * Displays splash screen, then checks authentication state
     */
    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        try {
            // Show splash screen for 3 seconds
            // await this.displaySplashScreen();

            // Check if user is already authenticated
            // await this.restoreUserSessionOrShowLogin();
        } catch (err) {
            console.error('❌ Welcome page initialization error:', err);
            await this.uxHelperService.hideSpinner();
        }
        return Promise.resolve();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    /**
     * Display splash screen for 3 seconds
     */
    // private async displaySplashScreen(): Promise<void> {
    //     this.splash = true;
    //     await new Promise(resolve => setTimeout(resolve, 3000));
    //     this.splash = false;
    // }

    /**
     * Handle "Go to Login" button click
     * Navigates to the login page
     */
    public async onLoginClick(): Promise<void> {
        try {
            // await this.invokeFirebaseFunctionForTesting();

            await this.uxHelperService.showSpinner();
            await this.processQueryParams();
            await this.restoreUserSessionOrShowLogin();
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Navigation Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    /**
     * Check if user is already logged in and restore session
     * If not, show the login/marketing slides
     */
    private async restoreUserSessionOrShowLogin(): Promise<void> {
        try {
            // For Cypress testing: force login screen
            if (this.forcePromptForLoginCredentials) {
                console.log('🧪 Test mode: forcing login screen');
                await this.router.navigateByUrl('/login');
                return;
            }

            await this.uxHelperService.showSpinner('Checking authentication...');

            // Check if we have a user token (from deep link, etc.)
            let user;
            if (this.userToken) {
                console.log('🔑 Logging in with token...');
                user = await this.authService.loginWithUserToken(this.userToken);
            } else {
                console.log('👤 Checking for existing user...');
                user = this.authService.getCurrentUser();
            }

            if (user) {
                console.log('✅ User found:', user.email);
                await this.handleAuthenticatedUser(user);
            } else {
                console.log('❌ No user found, showing login screen');
                await this.uxHelperService.hideSpinner();
                await this.router.navigateByUrl('/login');
            }
        } catch (err) {
            console.error('❌ Error restoring user session:', err);
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Authentication Error', errorMessage);
            await this.uxHelperService.hideSpinner();
            // Stay on welcome page
        }
    }

    /**
     * Handle authenticated user
     * Prepares session, publishes login event, and navigates to appropriate page
     */
    private async handleAuthenticatedUser(user: any): Promise<void> {
        try {
            console.log('🔄 Preparing user session...');
            
            // Load dealership data, settings, permissions, etc.
            await this.loaderService.prepareSession(user.uid);
            
            console.log('📢 Publishing LOGIN event...');
            
            // Notify AppComponent to update menu structure
            this.eventsService.publish(Constants.EVENT_LOGIN);
            
            // Small delay to ensure AppComponent processes the event
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // ✅ Close menu (if it was somehow opened)
            console.log('🚪 Ensuring menu is closed...');
            await this.menuController.close();

            // ✅ Remove focus from active element
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            // Determine where to navigate based on account status
            const navigationUrl = this.determineNavigationUrl();
            
            console.log('🚀 Navigating to:', navigationUrl);
            
            // ✅ CRITICAL FIX: Use replaceUrl to remove WelcomePage from stack
            const result = await this.router.navigateByUrl(navigationUrl, { 
                replaceUrl: true  // ← This removes WelcomePage from DOM
            });
                    
            console.log('📍 Navigation result:', result);

            if (!result) {
                console.error('❌ Navigation failed, trying with navigate()');
                await this.router.navigate([navigationUrl]);
            } else {
                console.log('✅ Navigation successful');
                await this.ensureRouterOutletVisible();
            }
        } catch (err) {
            console.error('❌ Error handling authenticated user:', err);
            throw err;
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    /**
     * Ensure router outlet is visible and doesn't have aria-hidden
     */
    // TODO: Is this method needed anymore?
    private async ensureRouterOutletVisible(): Promise<void> {
        // Wait a tick for DOM to update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const routerOutlet = document.querySelector('ion-router-outlet');
        
        if (routerOutlet) {
            // Remove aria-hidden if present
            if (routerOutlet.getAttribute('aria-hidden') === 'true') {
                console.log('🔧 Removing aria-hidden from router outlet');
                routerOutlet.removeAttribute('aria-hidden');
            }
            
            // Ensure it's visible
            const style = window.getComputedStyle(routerOutlet);
            if (style.display === 'none' || style.visibility === 'hidden') {
                console.log('🔧 Making router outlet visible');
                (routerOutlet as HTMLElement).style.display = '';
                (routerOutlet as HTMLElement).style.visibility = '';
            }
            
            console.log('✅ Router outlet is visible');
        } else {
            console.error('❌ Router outlet not found in DOM!');
        }
    }

    /**
     * Determine which page to navigate to based on account status
     */
    private determineNavigationUrl(): string {
        const status = this.sessionService.getAccountStatus();
        
        const expiredStatuses = [
            'Cancelled By Customer',
            'Cancelled By Support',
            'Trial Period Expired',
            'Package Expired'
        ];
        
        if (expiredStatuses.includes(status)) {
            console.log('⚠️ Account expired/cancelled, redirecting to access denied');
            return '/accessDenied';
        }
        
        // Active or In Trial Period
        console.log('✅ Account active, redirecting to dashboard');
        return '/dashboard';
    }

    // http://localhost:8100/welcome?forcePromptForLoginCredentials=true&setEmail=abc&setPassword=123&userToken=xxx
    private async processQueryParams(): Promise<void> {
        const urlParams = new URLSearchParams(window.location.search);

        urlParams.forEach(async (value, key) => {
            switch (key) {
                case 'forcePromptForLoginCredentials':
                    this.forcePromptForLoginCredentials = (value === 'true');
                    break;

                case 'setEmail':
                    await this.localStorageService.set('shuttleNomixLoginEmail', value);
                    break;

                case 'setPassword':
                    await this.localStorageService.set('shuttleNomixLoginPassword', value);
                    break;

                case 'userToken':
                    this.userToken = value;
                    // if(value.length != 0)
                    //     this.authService.loginWithUserToken(value);
                    break;
            }
        });
    }

    private async invokeFirebaseFunctionForTesting(): Promise<void> {
        try {
            // const response = await this.firebaseFunctionsService.testRewardFunnel();
            const response = await this.firebaseFunctionsService.testCreateAccount();
            console.log('Firebase function testCreateAccount response:', response);
        } catch (err) {
            console.error('Error invoking Firebase function testCreateAccount:', err);
        }
    }

    /**
     * Set user token for authentication
     * Used for deep linking or token-based login
     */
    public setUserToken(token: string): void {
        this.userToken = token;
    }

    /**
     * Force the app to show login screen
     * Used for Cypress testing
     */
    public setForceLogin(force: boolean): void {
        this.forcePromptForLoginCredentials = force;
    }
}